import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useOasisBackend } from '../../hooks/useOasisBackend';
import { useUser } from '../../hooks/useUser';
import { LoginItem } from '../../components/Login';
import { SignUpItem } from '../../components/SignupButton';
import { LogoutItem } from '../../components/LogoutButton';
import SettingsButton from '../components/SettingsButton';
import UnifiedSearchBar from '../components/unified-search/UnifiedSearchBar';
import { useLoggedOutUserSearchCounter } from '../hooks/useLoggedOutUserSearchCounter';
import type { ReadDatabase, DatabaseList } from '../../api/OasisBackendApi';

const Homepage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const user = useUser();
  const loggedIn = user.state === "LoggedIn";
  const { incrementSearchCount, LimitReachedMessage, hasReachedLimit, showLimitReached, setShowLimitReached, isInitialized } = useLoggedOutUserSearchCounter(loggedIn);
  const db = useOasisBackend();
  const [domains, setDomains] = useState<ReadDatabase[] | null>(null);
  const [agentParams, setAgentParams] = useState<{
    domain_id: string[];
    available_domains?: { id: string; name: string }[];
    deep_research?: boolean;
  }>({ domain_id: [] });

  // Fetch domains
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const response = await db.endpoints.rhea.rheaApiRheaAccountDatabasesRetrieve();
        const databaseList = response.data as DatabaseList;
        setDomains(databaseList.databases as ReadDatabase[]);
      } catch (error) {
        console.error('Error fetching domains:', error);
        setDomains([]);
      }
    };
    fetchDomains();
  }, [db]);

  // Get initial query from URL
  const initialQuery = searchParams.get('q') || '';

  // Initialize agentParams from URL if present (for page reloads)
  useEffect(() => {
    const params: {
      domain_id: string[];
      available_domains?: { id: string; name: string }[];
      deep_research?: boolean;
    } = { domain_id: [] };

    const domainId = searchParams.get('domain_id');
    if (domainId) {
      params.domain_id = domainId.split(',').filter(id => id.length > 0);
    }

    const availableDomains = searchParams.get('available_domains');
    if (availableDomains) {
      try {
        const decodedDomains = JSON.parse(availableDomains);
        if (Array.isArray(decodedDomains) && decodedDomains.length > 0) {
          params.available_domains = decodedDomains;
          if (!params.domain_id || params.domain_id.length === 0) {
            params.domain_id = [decodedDomains[0].id];
          }
        }
      } catch (error) {
        console.error('Error parsing available_domains from URL:', error);
      }
    }

    const deepResearch = searchParams.get('deep_research');
    if (deepResearch !== null) {
      params.deep_research = deepResearch === 'true';
    }

    if (params.domain_id.length > 0 || params.available_domains || params.deep_research !== undefined) {
      setAgentParams(params);
    }
  }, [searchParams]);

  // Perform search if there's an initial query
  useEffect(() => {
    if (initialQuery && isInitialized) {
      if (!loggedIn && hasReachedLimit) {
        setShowLimitReached(true);
        return;
      }
      handleSearch(initialQuery, {
        model: 'gpt-4.1-mini',
        agent: 'search_engine',
        agentParams: agentParams,
      });
    }
  }, [initialQuery, isInitialized, agentParams]);

  const handleSearch = (
    query: string, 
    params: { 
      model: string; 
      agent: string; 
      agentParams: { 
        domain_id: string | string[]; 
        available_domains?: { id: string; name: string }[];
        deep_research?: boolean;
      } 
    }
  ) => {
    if (!loggedIn && hasReachedLimit) {
      setShowLimitReached(true);
      return;
    }

    if (!loggedIn) {
      incrementSearchCount();
    }

    setIsLoading(true);

    try {
      if (params.agent === 'search_engine') {
        // For search engine, navigate to search page with all parameters
        const searchParams = new URLSearchParams();
        searchParams.set('q', query);
        searchParams.set('model', params.model);
        searchParams.set('agent', params.agent);
        
        // Add all agent parameters to URL
        Object.entries(params.agentParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (key === 'available_domains' && Array.isArray(value)) {
              // For available_domains, we need to preserve both id and name
              // We'll encode the full objects as JSON
              searchParams.set(key, JSON.stringify(value));
            } else if (key === 'deep_research') {
              // For deep_research, convert boolean to string
              searchParams.set(key, String(value));
            } else if (Array.isArray(value)) {
              // For other arrays (like domain_ids), join with commas
              searchParams.set(key, value.join(','));
            } else {
              searchParams.set(key, String(value));
            }
          }
        });

        console.log('Homepage_v1: Navigating to search with params:', {
          query,
          model: params.model,
          agent: params.agent,
          agentParams: params.agentParams,
          url: `/search?${searchParams.toString()}`
        });

        navigate(`/search?${searchParams.toString()}`);
      } else {
        // For other agents, navigate to chat
        const searchParams = new URLSearchParams();
        searchParams.set('message', query);
        searchParams.set('agent', params.agent);
        searchParams.set('model', params.model);
        
        // Add all agent parameters to URL
        Object.entries(params.agentParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (key === 'available_domains' && Array.isArray(value)) {
              // For available_domains, we need to preserve both id and name
              // We'll encode the full objects as JSON
              searchParams.set(key, JSON.stringify(value));
            } else if (key === 'deep_research') {
              // For deep_research, convert boolean to string
              searchParams.set(key, String(value));
            } else if (Array.isArray(value)) {
              // For other arrays (like domain_ids), join with commas
              searchParams.set(key, value.join(','));
            } else {
              searchParams.set(key, String(value));
            }
          }
        });

        // Navigate to chat with all parameters
        navigate(`/chat/new?${searchParams.toString()}`);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAgentParamsChange = (params: { domain_id: string[]; available_domains?: { id: string; name: string }[]; deep_research?: boolean }) => {
    setAgentParams(params);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--rhea-bg-primary)', color: 'var(--rhea-text-primary)' }}>
      <style>
        {`
          /* Transparent scrollbar styles */
          ::-webkit-scrollbar {
            display: none;
          }
          
          /* Firefox scrollbar styles */
          * {
            scrollbar-width: none;
          }
        `}
      </style>
      <LimitReachedMessage />
      
      {/* Show centered search when no content */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="text-center mb-8 w-full px-4">
          <div className="flex items-center justify-center gap-2 mb-8">
            <img src="/assets/rhea/logo.png" alt="Rhea" className="h-16" />
            <span className="text-5xl font-semibold" style={{ color: 'var(--rhea-text-primary)' }}>Rhea</span>
          </div>
          <div className="w-full max-w-6xl mx-auto">
            <UnifiedSearchBar 
              onSearch={handleSearch}
              isLoading={isLoading}
              initialValue={initialQuery}
              autoFocus={true}
              domains={domains}
              initialAgentParams={agentParams}
              onAgentParamsChange={handleAgentParamsChange}
            />
          </div>
        </div>
        <div className="absolute top-4 right-4 flex items-center gap-2">
          {!loggedIn ? (
            <>
              <LoginItem title="Login to ThoughtfulOasis" className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-card-border)] text-[var(--rhea-text-secondary)] hover:bg-[var(--rhea-card-hover)]"/>
              <SignUpItem title="Sign up for ThoughtfulOasis" className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-accent-primary)] text-[var(--rhea-accent-primary)] hover:bg-[var(--rhea-card-hover)]"/>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate('/chat/new')}
                className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-accent-primary)] text-[var(--rhea-accent-primary)] hover:bg-[var(--rhea-card-hover)]"
              >
                Chat
              </button>
              <SettingsButton />
              <LogoutItem className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-card-border)] text-[var(--rhea-text-secondary)] hover:bg-[var(--rhea-card-hover)]"/>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Homepage; 